import { useCallback, useEffect, useRef, useState } from 'react';

// Desconecta o usuário depois de 10 minutos sem interação. Um minuto antes, avisa e deixa
// continuar conectado. A última atividade fica no localStorage, então vale para todas as
// abas abertas e também quando o computador volta de suspensão ou a página é reaberta.
export const IDLE_LIMIT_MS = 10 * 60 * 1000;
const WARNING_MS = 60 * 1000;
const ACTIVITY_KEY = 'zsb_last_activity';
export const IDLE_LOGOUT_NOTICE_KEY = 'zsb_idle_logout';

const readActivity = () => { try { return Number(localStorage.getItem(ACTIVITY_KEY)) || 0; } catch { return 0; } };
const writeActivity = () => { try { localStorage.setItem(ACTIVITY_KEY, String(Date.now())); } catch { /* sem armazenamento: só vale nesta aba */ } };
const clearActivity = () => { try { localStorage.removeItem(ACTIVITY_KEY); } catch { /* ignora */ } };

// `signedIn`: há usuário logado. `ready`: o estado de login já foi resolvido (evita apagar a
// marca de atividade enquanto a sessão ainda está carregando).
export function useIdleLogout(signedIn: boolean, ready: boolean, logout: () => void | Promise<void>) {
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const logoutRef = useRef(logout);
  logoutRef.current = logout;
  const warningRef = useRef(false);
  warningRef.current = secondsLeft !== null;
  const lastWriteRef = useRef(0);

  useEffect(() => {
    if (!ready) return;
    if (!signedIn) { clearActivity(); setSecondsLeft(null); return; }

    // Primeira vez que o usuário aparece nesta carga da página: sem marca = login novo;
    // com marca antiga = sessão que ficou aberta (reabriu o navegador, por exemplo).
    const stored = readActivity();
    if (stored && Date.now() - stored >= IDLE_LIMIT_MS) {
      try { sessionStorage.setItem(IDLE_LOGOUT_NOTICE_KEY, '1'); } catch { /* ignora */ }
      clearActivity();
      void logoutRef.current();
      return;
    }
    if (!stored) writeActivity();

    const touch = () => {
      const now = Date.now();
      if (now - lastWriteRef.current < 2000) return; // não grava a cada movimento do mouse
      lastWriteRef.current = now;
      writeActivity();
    };
    // Durante o aviso, só um clique ou tecla conta; mover o mouse ou rolar não renova.
    const onActive = (event: Event) => {
      if (warningRef.current && (event.type === 'mousemove' || event.type === 'scroll' || event.type === 'wheel')) return;
      touch();
      if (warningRef.current) setSecondsLeft(null);
    };

    const tick = () => {
      const idle = Date.now() - (readActivity() || Date.now());
      if (idle >= IDLE_LIMIT_MS) {
        try { sessionStorage.setItem(IDLE_LOGOUT_NOTICE_KEY, '1'); } catch { /* ignora */ }
        clearActivity();
        setSecondsLeft(null);
        void logoutRef.current();
      } else if (idle >= IDLE_LIMIT_MS - WARNING_MS) {
        setSecondsLeft(Math.ceil((IDLE_LIMIT_MS - idle) / 1000));
      } else {
        setSecondsLeft(null);
      }
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'wheel', 'scroll'] as const;
    events.forEach((name) => window.addEventListener(name, onActive, { passive: true, capture: true }));
    const onVisible = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', tick);
    const interval = window.setInterval(tick, 1000);
    tick();
    return () => {
      events.forEach((name) => window.removeEventListener(name, onActive, { capture: true }));
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', tick);
      window.clearInterval(interval);
    };
  }, [signedIn, ready]);

  const stayConnected = useCallback(() => { writeActivity(); setSecondsLeft(null); }, []);
  return { secondsLeft, stayConnected };
}
